import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { Heart } from "@faire/slate-icons/Heart";
import { tsv } from "@faire/slate/lib/tsv";
import { AnimatePresence } from "framer-motion";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import RetailerLayout from "../../../components/RetailerLayout";
import DiscoveryProductTile from "../../../components/shared/DiscoveryProductTile";
import { CompassProvider } from "../../../contexts/CompassContext";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";
import { getEntityByToken } from "../../../data/get_entity";

import {
  type CollectionImageTransition,
  FavoriteCollectionPicker,
  type FavoriteCollection,
  type PopoverAnchorLocation,
} from "./FavoriteCollectionPicker";
import { FavoriteSavedToast } from "./FavoriteSavedToast";

const MemoizedDiscoveryProductTile = memo(DiscoveryProductTile);
MemoizedDiscoveryProductTile.displayName = "MemoizedDiscoveryProductTile";

export const prototypeMeta: PrototypeMetadata = {
  name: "Favorites popover interaction",
  owner: "Chris Rodriguez",
  email: "chrisleerod@gmail.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-03",
  lastUpdated: "2026-08-03",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "cursor",
  thumbnail:
    "https://cdn.prerelease.faire.dev/fastly/42d014dcde22ba1c146172f8d31224675d5efada3ac9c40cd08bd9e194ab1e5e.png",
  description:
    "Demonstrates an anchored favorites popover on desktop and a responsive drawer on mobile.",
  project: "Saved Products / Favorites",
};

const PRODUCT_TOKENS = [
  "p_8woyuyox",
  "p_dq0bo72g",
  "p_b256rebj",
  "p_u2nue4qfqt",
  "p_u7phhdyhub",
  "p_9iu6e71o",
  "p_bys2grkfzq",
  "p_1a4lqikhph",
  "p_8cr8xypu",
  "p_rvrvkn2wxy",
  "p_ehtht010",
  "p_pdyqc7yez2",
  "p_236jcn5z",
  "p_7y25fg3635",
  "p_3sdx5fxw",
  "p_m6zfg3dbw7",
  "p_q5kyq2xcup",
  "p_ymrqc4ecjy",
] as const;

const products = PRODUCT_TOKENS.map((productToken) =>
  getEntityByToken(productToken, "product")
).filter((product): product is FaireProductEntity => Boolean(product));

const FILTERS = [
  "All filters",
  "Low minimum",
  "Free shipping",
  "Bestsellers",
  "New products",
  "No import duties",
] as const;

const getProductImageUrl = (productToken: string) =>
  getEntityByToken(productToken, "product")?.imageUrl;

const ALL_FAVORITES_COLLECTION_ID = "all-favorites";
const EXTRA_COLLECTIONS_HIDDEN = 1;
const EXTRA_COLLECTIONS_VISIBLE = 2;
const POPOVER_ANCHOR_PRODUCT_CARD = 1;
const POPOVER_ANCHOR_HEART = 2;
const CLOSE_AFTER_SELECTION_OFF = 1;
const CLOSE_AFTER_SELECTION_ON = 2;
const FIRST_HEART_CLICK_OPENS_PICKER = 1;
const FIRST_HEART_CLICK_SAVES_WITH_TOAST = 2;
const INITIAL_ALL_FAVORITES_IMAGE_URL =
  getProductImageUrl("atc_refined_moma_9");

type ProductTokensByCollection = Record<string, string[]>;
type CollectionImageTransitions = Record<string, CollectionImageTransition>;
type PopoverAnchorLocationState =
  | typeof POPOVER_ANCHOR_PRODUCT_CARD
  | typeof POPOVER_ANCHOR_HEART;

const INITIAL_COLLECTIONS: FavoriteCollection[] = [
  {
    id: ALL_FAVORITES_COLLECTION_ID,
    name: "All favorites",
    imageUrl: INITIAL_ALL_FAVORITES_IMAGE_URL,
  },
  {
    id: "kitchen-fall-2026",
    name: "Kitchen, Fall 2026",
    imageUrl: getProductImageUrl("catalog_prod_prod-19"),
  },
  {
    id: "japanese-market",
    name: "Japanese market",
    imageUrl: getProductImageUrl("p_kubhyw672d"),
  },
  {
    id: "fall-chocolates",
    name: "Fall chocolates",
    imageUrl: getProductImageUrl("p_z67nypuv9s"),
  },
  {
    id: "holiday-cards",
    name: "Holiday cards",
    imageUrl: getProductImageUrl("p_hm44x2pgpm"),
  },
  {
    id: "fall-scents",
    name: "Fall scents",
    imageUrl: getProductImageUrl("p_c6457xt61l"),
  },
];

const SEEDED_EXTRA_COLLECTION_IDS = new Set(
  INITIAL_COLLECTIONS.filter(
    (collection) => collection.id !== ALL_FAVORITES_COLLECTION_ID
  ).map((collection) => collection.id)
);

let collectionImagesReadyPromise: Promise<void> | undefined;

const preloadCollectionImages = (): Promise<void> => {
  collectionImagesReadyPromise ??= Promise.all(
    INITIAL_COLLECTIONS.map(async (collection) => {
      if (!collection.imageUrl) {
        return;
      }
      const image = new Image();
      image.src = collection.imageUrl;
      await image.decode().catch(() => undefined);
    })
  ).then(() => undefined);
  return collectionImagesReadyPromise;
};

export default function FavoritesPopoverInteractionPage() {
  const locationControlTitle = usePlaygroundLocalMsg("Location");
  const productCardLocationLabel = usePlaygroundLocalMsg("Product card");
  const heartLocationLabel = usePlaygroundLocalMsg("Heart");
  const closeAfterSelectionTitle = usePlaygroundLocalMsg(
    "Close after selection"
  );
  const offLabel = usePlaygroundLocalMsg("Off");
  const onLabel = usePlaygroundLocalMsg("On");
  const firstHeartClickTitle = usePlaygroundLocalMsg("First heart click");
  const openCollectionsLabel = usePlaygroundLocalMsg("Open collections");
  const saveWithToastLabel = usePlaygroundLocalMsg("Save + toast");
  const allFavoritesLabel = usePlaygroundLocalMsg("All favorites");
  const productCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const favoriteButtonRefs = useRef<Record<string, HTMLButtonElement | null>>(
    {}
  );
  const activeProductTokenRef = useRef<string | null>(null);
  const isPickerOpenRef = useRef(false);
  const nextCollectionNumber = useRef(1);
  const nextCollectionImageTransitionId = useRef(1);
  const pendingAllFavoritesImageRef = useRef<{
    imageUrl: string | undefined;
  } | null>(null);
  const [activeProductToken, setActiveProductToken] = useState<string | null>(
    null
  );
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isPickerRendered, setIsPickerRendered] = useState(false);
  const [extraCollectionsState, setExtraCollectionsState] = useState(
    EXTRA_COLLECTIONS_VISIBLE
  );
  const [popoverAnchorLocation, setPopoverAnchorLocation] =
    useState<PopoverAnchorLocationState>(POPOVER_ANCHOR_PRODUCT_CARD);
  const [closeAfterSelectionState, setCloseAfterSelectionState] = useState(
    CLOSE_AFTER_SELECTION_OFF
  );
  const [firstHeartClickState, setFirstHeartClickState] = useState(
    FIRST_HEART_CLICK_OPENS_PICKER
  );
  const [collections, setCollections] =
    useState<FavoriteCollection[]>(INITIAL_COLLECTIONS);
  const [productTokensByCollection, setProductTokensByCollection] =
    useState<ProductTokensByCollection>({});
  const [collectionImageTransitions, setCollectionImageTransitions] =
    useState<CollectionImageTransitions>({});
  const [savedProductToast, setSavedProductToast] = useState<{
    collectionName: string;
    imageUrl: string;
  } | null>(null);

  useEffect(() => {
    document.title = prototypeMeta.name;
    void preloadCollectionImages();
  }, []);

  useEffect(() => {
    if (!savedProductToast) {
      return;
    }
    const timeout = window.setTimeout(() => setSavedProductToast(null), 5000);
    return () => window.clearTimeout(timeout);
  }, [savedProductToast]);

  const handlePickerClose = useCallback(() => {
    isPickerOpenRef.current = false;
    setIsPickerOpen(false);
  }, []);

  const handlePickerExitComplete = useCallback(() => {
    setIsPickerRendered(false);
    const pendingAllFavoritesImage = pendingAllFavoritesImageRef.current;
    if (!pendingAllFavoritesImage) {
      return;
    }
    pendingAllFavoritesImageRef.current = null;
    setCollections((currentCollections) =>
      updateAllFavoritesCollectionImage(
        currentCollections,
        pendingAllFavoritesImage.imageUrl
      )
    );
  }, []);

  const registerProductCard = useCallback(
    (productToken: string, productCard: HTMLDivElement | null) => {
      productCardRefs.current[productToken] = productCard;
    },
    []
  );

  const registerFavoriteButton = useCallback(
    (productToken: string, favoriteButton: HTMLButtonElement | null) => {
      favoriteButtonRefs.current[productToken] = favoriteButton;
    },
    []
  );

  const getAnchorElement = useCallback(
    (productToken: string, anchorLocation: PopoverAnchorLocationState) =>
      anchorLocation === POPOVER_ANCHOR_HEART
        ? favoriteButtonRefs.current[productToken]
        : productCardRefs.current[productToken],
    []
  );

  const handlePopoverAnchorLocationChange = useCallback(
    (anchorLocation: number) => {
      const nextAnchorLocation =
        anchorLocation === POPOVER_ANCHOR_HEART
          ? POPOVER_ANCHOR_HEART
          : POPOVER_ANCHOR_PRODUCT_CARD;
      setPopoverAnchorLocation(nextAnchorLocation);

      const activeProductToken = activeProductTokenRef.current;
      if (!activeProductToken || !isPickerOpenRef.current) {
        return;
      }
      const nextAnchorElement = getAnchorElement(
        activeProductToken,
        nextAnchorLocation
      );
      if (nextAnchorElement) {
        setAnchorElement(nextAnchorElement);
      }
    },
    [getAnchorElement]
  );

  const startCollectionImageTransition = useCallback(
    (
      collectionId: string,
      direction: CollectionImageTransition["direction"],
      coverImageUrl: string,
      backgroundImageUrl: string | undefined
    ) => {
      const transitionId = nextCollectionImageTransitionId.current;
      nextCollectionImageTransitionId.current += 1;
      setCollectionImageTransitions((currentImageTransitions) => ({
        ...currentImageTransitions,
        [collectionId]: {
          backgroundImageUrl,
          coverImageUrl,
          direction,
          id: transitionId,
        },
      }));
    },
    []
  );

  const handleCollectionImageTransitionComplete = useCallback(
    (collectionId: string, transitionId: number) => {
      setCollectionImageTransitions((currentImageTransitions) => {
        if (currentImageTransitions[collectionId]?.id !== transitionId) {
          return currentImageTransitions;
        }
        const nextImageTransitions = { ...currentImageTransitions };
        delete nextImageTransitions[collectionId];
        return nextImageTransitions;
      });
    },
    []
  );

  const handleFavoriteClick = useCallback(
    (productToken: string, isFavorited: boolean) => {
      const nextAnchorElement = getAnchorElement(
        productToken,
        popoverAnchorLocation
      );
      if (!nextAnchorElement) {
        return;
      }
      if (
        isPickerOpenRef.current &&
        activeProductTokenRef.current === productToken
      ) {
        handlePickerClose();
        return;
      }

      if (!isFavorited) {
        const productImageUrl = getProductImageUrl(productToken);
        pendingAllFavoritesImageRef.current = null;
        setCollections((currentCollections) =>
          updateAllFavoritesCollectionImage(currentCollections, productImageUrl)
        );
        setProductTokensByCollection((currentProductTokensByCollection) =>
          addProductToCollection(
            currentProductTokensByCollection,
            ALL_FAVORITES_COLLECTION_ID,
            productToken
          )
        );
        if (
          firstHeartClickState === FIRST_HEART_CLICK_SAVES_WITH_TOAST &&
          productImageUrl
        ) {
          if (isPickerOpenRef.current) {
            handlePickerClose();
          }
          setSavedProductToast({
            collectionName: allFavoritesLabel,
            imageUrl: productImageUrl,
          });
          return;
        }
      }
      setSavedProductToast(null);
      void preloadCollectionImages().then(() => {
        activeProductTokenRef.current = productToken;
        isPickerOpenRef.current = true;
        setActiveProductToken(productToken);
        setAnchorElement(nextAnchorElement);
        setIsPickerRendered(true);
        setIsPickerOpen(true);
      });
    },
    [
      allFavoritesLabel,
      firstHeartClickState,
      getAnchorElement,
      handlePickerClose,
      popoverAnchorLocation,
    ]
  );

  const handleToggleCollection = (collectionId: string) => {
    if (!activeProductToken) {
      return;
    }

    const removesProductFromAllFavorites =
      collectionId === ALL_FAVORITES_COLLECTION_ID &&
      selectedCollectionIds.includes(ALL_FAVORITES_COLLECTION_ID);
    if (removesProductFromAllFavorites) {
      const remainingFavoriteProductTokens = (
        productTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? []
      ).filter((productToken) => productToken !== activeProductToken);
      const latestFavoriteProductToken =
        remainingFavoriteProductTokens[
          remainingFavoriteProductTokens.length - 1
        ];
      pendingAllFavoritesImageRef.current = {
        imageUrl: latestFavoriteProductToken
          ? getProductImageUrl(latestFavoriteProductToken)
          : INITIAL_ALL_FAVORITES_IMAGE_URL,
      };
    }

    const isRemovingProduct = selectedCollectionIds.includes(collectionId);
    const activeProductImageUrl = getProductImageUrl(activeProductToken);
    const selectedCollection = collections.find(
      (collection) => collection.id === collectionId
    );
    const selectedCollectionProductTokens =
      productTokensByCollection[collectionId] ?? [];
    const latestSelectedCollectionProductToken =
      selectedCollectionProductTokens[
        selectedCollectionProductTokens.length - 1
      ];
    if (
      !removesProductFromAllFavorites &&
      selectedCollection &&
      activeProductImageUrl
    ) {
      if (
        isRemovingProduct &&
        latestSelectedCollectionProductToken === activeProductToken
      ) {
        const remainingCollectionProductTokens =
          selectedCollectionProductTokens.filter(
            (productToken) => productToken !== activeProductToken
          );
        const nextLatestProductToken =
          remainingCollectionProductTokens[
            remainingCollectionProductTokens.length - 1
          ];
        startCollectionImageTransition(
          collectionId,
          "remove",
          activeProductImageUrl,
          nextLatestProductToken
            ? getProductImageUrl(nextLatestProductToken)
            : selectedCollection.imageUrl
        );
      } else if (!isRemovingProduct) {
        startCollectionImageTransition(
          collectionId,
          "add",
          activeProductImageUrl,
          latestSelectedCollectionProductToken
            ? getProductImageUrl(latestSelectedCollectionProductToken)
            : selectedCollection.imageUrl
        );
      }
    }

    const addsProductToAllFavorites =
      !isRemovingProduct &&
      (collectionId === ALL_FAVORITES_COLLECTION_ID ||
        !selectedCollectionIds.includes(ALL_FAVORITES_COLLECTION_ID));
    if (
      addsProductToAllFavorites &&
      collectionId !== ALL_FAVORITES_COLLECTION_ID &&
      activeProductImageUrl
    ) {
      const allFavoritesCollection = collections.find(
        (collection) => collection.id === ALL_FAVORITES_COLLECTION_ID
      );
      const allFavoriteProductTokens =
        productTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? [];
      const latestFavoriteProductToken =
        allFavoriteProductTokens[allFavoriteProductTokens.length - 1];
      startCollectionImageTransition(
        ALL_FAVORITES_COLLECTION_ID,
        "add",
        activeProductImageUrl,
        latestFavoriteProductToken
          ? getProductImageUrl(latestFavoriteProductToken)
          : allFavoritesCollection?.imageUrl
      );
    }
    if (addsProductToAllFavorites) {
      pendingAllFavoritesImageRef.current = null;
      setCollections((currentCollections) =>
        updateAllFavoritesCollectionImage(
          currentCollections,
          getProductImageUrl(activeProductToken)
        )
      );
    }

    setProductTokensByCollection((currentProductTokensByCollection) => {
      if (removesProductFromAllFavorites) {
        const nextProductTokensByCollection = {
          ...currentProductTokensByCollection,
        };
        selectedCollectionIds.forEach((selectedCollectionId) => {
          nextProductTokensByCollection[selectedCollectionId] = (
            currentProductTokensByCollection[selectedCollectionId] ?? []
          ).filter((productToken) => productToken !== activeProductToken);
        });
        return nextProductTokensByCollection;
      }

      if (isRemovingProduct) {
        return removeProductFromCollection(
          currentProductTokensByCollection,
          collectionId,
          activeProductToken
        );
      }

      const productTokensWithSelectedCollection = addProductToCollection(
        currentProductTokensByCollection,
        collectionId,
        activeProductToken
      );
      return collectionId !== ALL_FAVORITES_COLLECTION_ID &&
        !selectedCollectionIds.includes(ALL_FAVORITES_COLLECTION_ID)
        ? addProductToCollection(
            productTokensWithSelectedCollection,
            ALL_FAVORITES_COLLECTION_ID,
            activeProductToken
          )
        : productTokensWithSelectedCollection;
    });
  };

  const handleCreateCollection = (collectionName: string) => {
    if (!activeProductToken) {
      return;
    }

    const collectionId = `new-collection-${nextCollectionNumber.current}`;
    const activeProduct = products.find(
      (product) => product.token === activeProductToken
    );
    if (!activeProduct) {
      return;
    }
    nextCollectionNumber.current += 1;
    setCollections((currentCollections) => {
      const newCollectionIndex =
        currentCollections.findIndex(
          (collection) => collection.id === ALL_FAVORITES_COLLECTION_ID
        ) + 1;

      return [
        ...currentCollections.slice(0, newCollectionIndex),
        {
          id: collectionId,
          name: collectionName,
        },
        ...currentCollections.slice(newCollectionIndex),
      ];
    });
    setProductTokensByCollection((currentProductTokensByCollection) => {
      const productTokensWithAllFavorites = (
        currentProductTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? []
      ).includes(activeProductToken)
        ? currentProductTokensByCollection
        : addProductToCollection(
            currentProductTokensByCollection,
            ALL_FAVORITES_COLLECTION_ID,
            activeProductToken
          );
      return addProductToCollection(
        productTokensWithAllFavorites,
        collectionId,
        activeProductToken
      );
    });
    setSavedProductToast({
      collectionName,
      imageUrl: activeProduct.imageUrl,
    });
    handlePickerClose();
  };

  const selectedCollectionIds = activeProductToken
    ? collections
        .filter((collection) =>
          (productTokensByCollection[collection.id] ?? []).includes(
            activeProductToken
          )
        )
        .map((collection) => collection.id)
    : [];
  const activeProduct = activeProductToken
    ? products.find((product) => product.token === activeProductToken)
    : undefined;
  const collectionsWithCurrentImages = collections.map((collection) => {
    const imageUrl =
      collection.id === ALL_FAVORITES_COLLECTION_ID
        ? collection.imageUrl
        : (getLatestCollectionImageUrl(
            collection.id,
            productTokensByCollection
          ) ?? collection.imageUrl);
    const imageTransition = collectionImageTransitions[collection.id];
    return {
      ...collection,
      ...(imageTransition ? { imageTransition } : {}),
      imageUrl,
    };
  });
  const visibleCollections =
    extraCollectionsState === EXTRA_COLLECTIONS_VISIBLE
      ? collectionsWithCurrentImages
      : collectionsWithCurrentImages.filter(
          (collection) => !SEEDED_EXTRA_COLLECTION_IDS.has(collection.id)
        );
  const pickerAnchorLocation: PopoverAnchorLocation =
    popoverAnchorLocation === POPOVER_ANCHOR_HEART ? "heart" : "product-card";

  return (
    <>
      <LiveToolbar
        stateGroups={[
          {
            title: "Extra collections",
            queryKey: "extra-collections",
            states: [
              { stateNumber: EXTRA_COLLECTIONS_HIDDEN, label: "Off" },
              { stateNumber: EXTRA_COLLECTIONS_VISIBLE, label: "On" },
            ],
            active: extraCollectionsState,
            onChange: setExtraCollectionsState,
            splitButtonMode: true,
          },
          {
            title: locationControlTitle,
            queryKey: "popover-anchor",
            states: [
              {
                stateNumber: POPOVER_ANCHOR_PRODUCT_CARD,
                label: productCardLocationLabel,
              },
              {
                stateNumber: POPOVER_ANCHOR_HEART,
                label: heartLocationLabel,
              },
            ],
            active: popoverAnchorLocation,
            onChange: handlePopoverAnchorLocationChange,
            splitButtonMode: true,
          },
          {
            title: closeAfterSelectionTitle,
            queryKey: "close-after-selection",
            states: [
              {
                stateNumber: CLOSE_AFTER_SELECTION_OFF,
                label: offLabel,
              },
              {
                stateNumber: CLOSE_AFTER_SELECTION_ON,
                label: onLabel,
              },
            ],
            active: closeAfterSelectionState,
            onChange: setCloseAfterSelectionState,
            splitButtonMode: true,
          },
          {
            title: firstHeartClickTitle,
            queryKey: "first-heart-click",
            states: [
              {
                stateNumber: FIRST_HEART_CLICK_OPENS_PICKER,
                label: openCollectionsLabel,
              },
              {
                stateNumber: FIRST_HEART_CLICK_SAVES_WITH_TOAST,
                label: saveWithToastLabel,
              },
            ],
            active: firstHeartClickState,
            onChange: setFirstHeartClickState,
            splitButtonMode: true,
          },
        ]}
      />
      <CompassProvider>
        <RetailerLayout defaultSearchValue="cards">
          <main className={pageStyle.cls()} style={{ maxWidth: 1440 }}>
            <div className={filterRowStyle.cls()} aria-label="Product filters">
              {FILTERS.map((filter) => (
                <button
                  key={filter}
                  className={filterChipStyle.cls()}
                  type="button"
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className={productGridStyle.cls()}>
              {products.map((product) => (
                <FavoriteProductCard
                  isFavorited={
                    productTokensByCollection[
                      ALL_FAVORITES_COLLECTION_ID
                    ]?.includes(product.token) ?? false
                  }
                  key={product.token}
                  onFavorite={handleFavoriteClick}
                  product={product}
                  registerFavoriteButton={registerFavoriteButton}
                  registerProductCard={registerProductCard}
                />
              ))}
            </div>
          </main>
        </RetailerLayout>
        {isPickerRendered && activeProduct && anchorElement ? (
          <FavoriteCollectionPicker
            anchorElement={anchorElement}
            anchorLocation={pickerAnchorLocation}
            closeOnCollectionSelect={
              closeAfterSelectionState === CLOSE_AFTER_SELECTION_ON
            }
            collections={visibleCollections}
            key={activeProduct.token}
            onClose={handlePickerClose}
            onCollectionImageTransitionComplete={
              handleCollectionImageTransitionComplete
            }
            onCreateCollection={handleCreateCollection}
            onExitComplete={handlePickerExitComplete}
            onToggleCollection={handleToggleCollection}
            open={isPickerOpen}
            productImageUrl={activeProduct.imageUrl}
            selectedCollectionIds={selectedCollectionIds}
          />
        ) : null}
        <AnimatePresence>
          {savedProductToast ? (
            <FavoriteSavedToast
              collectionName={savedProductToast.collectionName}
              imageUrl={savedProductToast.imageUrl}
              key={`${savedProductToast.collectionName}-${savedProductToast.imageUrl}`}
              onClose={() => setSavedProductToast(null)}
              onView={() => setSavedProductToast(null)}
            />
          ) : null}
        </AnimatePresence>
      </CompassProvider>
    </>
  );
}

interface FavoriteProductCardProps {
  isFavorited: boolean;
  product: FaireProductEntity;
  onFavorite: (productToken: string, isFavorited: boolean) => void;
  registerFavoriteButton: (
    productToken: string,
    favoriteButton: HTMLButtonElement | null
  ) => void;
  registerProductCard: (
    productToken: string,
    productCard: HTMLDivElement | null
  ) => void;
}

const FavoriteProductCard = memo(
  ({
    isFavorited,
    product,
    onFavorite,
    registerFavoriteButton,
    registerProductCard,
  }: FavoriteProductCardProps) => (
    <div
      className="relative"
      onMouseDownCapture={(event) => {
        const target = event.target;
        if (
          target instanceof Element &&
          target.closest("[data-favorite-button]")
        ) {
          event.stopPropagation();
        }
      }}
      ref={(productCard) => registerProductCard(product.token, productCard)}
    >
      <MemoizedDiscoveryProductTile
        badge={product.isBestseller ? "Bestseller" : undefined}
        brandName={product.brandName}
        freeShipping={product.freeShipping}
        hideHeart
        imageUrl={product.imageUrl}
        msrp={product.msrpDollars}
        price={product.priceDollars}
        productName={product.name}
        rating={product.rating}
        showQuickAdd
      />
      <button
        aria-label={
          isFavorited
            ? "Remove product from favorites"
            : "Save product to favorites"
        }
        className={favoriteButtonStyle.cls()}
        data-favorite-button
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onFavorite(product.token, isFavorited);
        }}
        style={{ fontSize: "24px" }}
        ref={(favoriteButton) =>
          registerFavoriteButton(product.token, favoriteButton)
        }
        type="button"
      >
        <Heart className={favoriteIconStyle.cls({ isFavorited })} />
      </button>
    </div>
  )
);
FavoriteProductCard.displayName = "FavoriteProductCard";

const getLatestCollectionImageUrl = (
  collectionId: string,
  productTokensByCollection: ProductTokensByCollection
): string | undefined => {
  const collectionProductTokens = productTokensByCollection[collectionId] ?? [];
  const latestProductToken =
    collectionProductTokens[collectionProductTokens.length - 1];
  return latestProductToken
    ? getProductImageUrl(latestProductToken)
    : undefined;
};

const addProductToCollection = (
  productTokensByCollection: ProductTokensByCollection,
  collectionId: string,
  productToken: string
): ProductTokensByCollection => ({
  ...productTokensByCollection,
  [collectionId]: [
    ...(productTokensByCollection[collectionId] ?? []).filter(
      (collectionProductToken) => collectionProductToken !== productToken
    ),
    productToken,
  ],
});

const removeProductFromCollection = (
  productTokensByCollection: ProductTokensByCollection,
  collectionId: string,
  productToken: string
): ProductTokensByCollection => ({
  ...productTokensByCollection,
  [collectionId]: (productTokensByCollection[collectionId] ?? []).filter(
    (collectionProductToken) => collectionProductToken !== productToken
  ),
});

const updateAllFavoritesCollectionImage = (
  collections: FavoriteCollection[],
  imageUrl: string | undefined
): FavoriteCollection[] =>
  collections.map((collection) =>
    collection.id === ALL_FAVORITES_COLLECTION_ID
      ? { ...collection, imageUrl }
      : collection
  );

const pageStyle = tsv({
  base: [
    "bg-fs-surface-primary mx-auto min-h-screen w-full",
    "px-(--slate-spacing-layout-edge-mobile) py-(--slate-spacing-section-small)",
    "md:px-(--slate-spacing-layout-edge-tablet)",
    "lg:px-(--slate-spacing-layout-edge-desktop-default)",
  ],
});

const filterRowStyle = tsv({
  base: [
    "mb-(--slate-spacing-group-xlarge) flex flex-wrap",
    "gap-(--slate-spacing-group-small)",
  ],
});

const filterChipStyle = tsv({
  base: [
    "border-fs-border-muted bg-fs-surface-primary rounded-fs-component-round border",
    "type-fs-label text-fs-action-text-default",
    "px-(--slate-spacing-element-medium) py-(--slate-spacing-element-small)",
    "hover:bg-fs-action-surface-inverse-hover focusable interactive",
  ],
});

const productGridStyle = tsv({
  base: [
    "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
    "gap-x-(--slate-spacing-group-large) gap-y-(--slate-spacing-section-small)",
  ],
});

const favoriteButtonStyle = tsv({
  base: [
    "group absolute top-1 right-1 z-3 m-1 flex items-center justify-center",
    "transition-all duration-50 ease-in enabled:active:scale-[1.2]",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[-webkit-focus-ring-color]",
  ],
});

const favoriteIconStyle = tsv({
  base: [
    "[&_path]:stroke-1",
    "[&_path]:stroke-fs-action-icon-inverse [&_path]:fill-fs-action-icon-default",
  ],
  variants: {
    isFavorited: {
      true: [
        "[&_path]:[fill-opacity:1]",
        "[@media(hover:hover)_and_(pointer:fine)]:group-hover:[&_path]:[fill-opacity:1]",
      ],
      false: [
        "[&_path]:[fill-opacity:0.3]",
        "[@media(hover:hover)_and_(pointer:fine)]:group-hover:[&_path]:[fill-opacity:0.8]",
      ],
    },
  },
});
